import gsap from "gsap";
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger);

function bannerStrategicPlan() {
	const section = document.querySelector('[data-name="banner-strategic-plan"]');
	if (!section) return;

	const image = section.querySelector('[data-name="bsp-image"]');
	const titleFr = section.querySelector('[data-name="bsp-title-fr"]');
	const titleEn = section.querySelector('[data-name="bsp-title"]');
	const body = section.querySelector('[data-name="bsp-body"]');
	const scrollHintOuter = section.querySelector('[data-name="bsp-scroll-hint-outer"]');
	const scrollHintInner = section.querySelector('[data-name="bsp-scroll-hint-inner"]');
	const breadcrumb = section.querySelector('[data-name="bsp-breadcrumb"]');
	const titlesWrap = section.querySelector('[data-name="bsp-titles"]');
	const hintBox = scrollHintOuter?.querySelector('div') || scrollHintInner?.querySelector('a');
	const hintLink = scrollHintInner?.querySelector('a[href="#strategic-plan-intro"]');
	const introTarget = document.getElementById('strategic-plan-intro');
	const titleEls = [breadcrumb, titleFr, titleEn].filter(Boolean);
	const hintEls = [scrollHintOuter, scrollHintInner].filter(Boolean);
	const titleFadeEls = section.querySelectorAll('[data-name="bsp-title-fade"]');
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const isDesktop = () => window.matchMedia('(min-width: 992px)').matches;
	const imageOpacity = () => (isDesktop() ? 0.7 : 0.5);
	const getLenis = () => window.lenis || window._lenis;
	const scrollLockOpts = { passive: false, capture: true };

	const preventScroll = (event) => {
		event.preventDefault();
	};

	const preventScrollKeys = (event) => {
		const keys = [ ' ', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End' ];
		if (keys.includes(event.key)) {
			event.preventDefault();
		}
	};

	const lockScroll = () => {
		ScrollTrigger.normalizeScroll(false);
		getLenis()?.stop();
		document.documentElement.style.overflow = 'hidden';
		document.body.style.overflow = 'hidden';
		window.addEventListener('wheel', preventScroll, scrollLockOpts);
		window.addEventListener('touchmove', preventScroll, scrollLockOpts);
		window.addEventListener('keydown', preventScrollKeys, scrollLockOpts);
	};

	const unlockScroll = () => {
		window.removeEventListener('wheel', preventScroll, scrollLockOpts);
		window.removeEventListener('touchmove', preventScroll, scrollLockOpts);
		window.removeEventListener('keydown', preventScrollKeys, scrollLockOpts);
		document.documentElement.style.overflow = '';
		document.body.style.overflow = '';
		getLenis()?.start();
		ScrollTrigger.normalizeScroll(true);
	};

	const getRestTop = () => {
		if (!isDesktop()) return 0;
		const desired = Math.max(window.innerHeight * 0.32, 180);
		if (!titlesWrap || !hintBox) return desired;
		const maxTop = hintBox.getBoundingClientRect().top - titlesWrap.getBoundingClientRect().bottom - 20;
		return Math.max(0, Math.min(desired, maxTop));
	};

	const revealEls = () => {
		if (image) {
			image.classList.remove('opacity-0');
			image.classList.add('opacity-50', 'lg:opacity-70');
		}
		if (breadcrumb) breadcrumb.classList.remove('opacity-0');
		titleFadeEls.forEach((el) => el.classList.remove('opacity-0'));
		hintEls.forEach((el) => el.classList.remove('opacity-0'));
	};

	const setupPin = () => {
		const mm = gsap.matchMedia();

		mm.add('(min-width: 992px)', () => {
			const restTop = () => getRestTop();
			const titleEnEndY = () => Math.max(0, Math.min(100, restTop()));
			const imageEndY = () => window.innerHeight * 0.5;

			gsap.set(titleEls, { position: 'relative', top: restTop() });
			if (body) gsap.set(body, { autoAlpha: 0 });

			const tl = gsap.timeline({
				scrollTrigger: {
					id: 'bsp-pin',
					trigger: section,
					start: 'top top',
					end: () => `+=${window.innerHeight}`,
					pin: true,
					scrub: true,
					anticipatePin: 1,
					invalidateOnRefresh: true,
					onRefresh: (self) => {
						if (self.progress === 0) {
							gsap.set(titleEls, { position: 'relative', top: restTop() });
						}
					},
				},
			});

			if (image) {
				tl.fromTo(image, { marginTop: 0, opacity: 0.7 }, {
					marginTop: imageEndY,
					opacity: 0.3,
					ease: 'none',
					duration: 1,
				}, 0);
			}

			if (breadcrumb) {
				tl.fromTo(breadcrumb, { top: restTop }, { top: 0, ease: 'none', duration: 0.65 }, 0);
			}

			if (titleFr) {
				tl.fromTo(titleFr, { top: restTop }, { top: 0, ease: 'none', duration: 0.65 }, 0);
			}

			if (titleEn) {
				tl.fromTo(titleEn, { top: restTop }, { top: titleEnEndY, ease: 'none', duration: 1 }, 0);
			}

			if (body) {
				tl.to(body, { autoAlpha: 1, ease: 'none', duration: 0.4 }, 0.6);
			}

			if (hintEls.length) {
				tl.fromTo(hintEls, { opacity: 1 }, { opacity: 0, ease: 'none', duration: 0.2 }, 0);
			}

			const onResize = () => {
				const pin = ScrollTrigger.getById('bsp-pin');
				if (!pin || pin.progress === 0) {
					gsap.set(titleEls, { position: 'relative', top: restTop() });
				}
				ScrollTrigger.refresh();
			};

			window.addEventListener('resize', onResize);

			return () => {
				window.removeEventListener('resize', onResize);
				tl.kill();
				gsap.set(titleEls, { clearProps: 'position,top' });
				if (body) gsap.set(body, { clearProps: 'opacity,visibility' });
				if (image) gsap.set(image, { clearProps: 'marginTop,opacity' });
			};
		});

		mm.add('(max-width: 991px)', () => {
			if (body) gsap.set(body, { autoAlpha: 1 });
			gsap.set(titleEls, { clearProps: 'position,top' });
			if (image) gsap.set(image, { clearProps: 'marginTop' });

			return () => {
				if (body) gsap.set(body, { clearProps: 'opacity,visibility' });
			};
		});
	};

	let pinReady = false;
	let pendingHintScroll = false;
	let hintScrolling = false;

	const lenisScrollTo = (targetY, duration, onComplete) => {
		const lenis = getLenis();
		if (lenis?.scrollTo) {
			lenis.scrollTo(targetY, {
				duration,
				lock: true,
				onComplete,
			});
			return;
		}
		window.scrollTo({ top: targetY, behavior: duration ? 'smooth' : 'auto' });
		if (onComplete) {
			window.setTimeout(onComplete, duration ? duration * 1000 : 0);
		}
	};

	const introOffset = 50;

	const getIntroScrollY = () => {
		if (!introTarget) return window.scrollY;
		return introTarget.getBoundingClientRect().top + window.scrollY - introOffset;
	};

	const playPinThenIntro = () => {
		if (hintScrolling) return;
		hintScrolling = true;

		const done = () => {
			hintScrolling = false;
		};

		const pin = ScrollTrigger.getById('bsp-pin');
		if (!isDesktop() || !pin || pin.progress >= 0.999) {
			lenisScrollTo(getIntroScrollY(), 1.2, done);
			return;
		}

		const introTop = introTarget ? introTarget.getBoundingClientRect().top : 0;
		const targetY = pin.end + Math.max(0, introTop - introOffset);
		lenisScrollTo(targetY, 1.5, done);
	};

	if (hintLink) {
		hintLink.addEventListener('click', (event) => {
			event.preventDefault();
			if (!pinReady) {
				pendingHintScroll = true;
				return;
			}
			playPinThenIntro();
		});
	}

	const finishIntro = () => {
		if (image) gsap.set(image, { clearProps: 'y,yPercent,transform' });
		revealEls();
		unlockScroll();
		setupPin();
		pinReady = true;
		if (pendingHintScroll) {
			pendingHintScroll = false;
			playPinThenIntro();
		}
	};

	if (prefersReducedMotion) {
		gsap.set(titleEls, { position: 'relative', top: getRestTop() });
		finishIntro();
		return;
	}

	lockScroll();

	gsap.set(titleEls, { position: 'relative', top: getRestTop() });

	const intro = gsap.timeline({
		defaults: { ease: 'power3.out' },
		onComplete: finishIntro,
	});

	if (image) {
		intro.fromTo(image, { yPercent: 24, opacity: 0 }, {
			yPercent: 0,
			opacity: imageOpacity(),
			duration: 2,
		});
	}

	const fadeEls = [ breadcrumb, ...titleFadeEls, ...hintEls ].filter(Boolean);
	if (fadeEls.length) {
		intro.to(fadeEls, { opacity: 1, duration: 2 }, 1);
	}
}

function bannerSkipLinks(){
	const allSections = document.querySelectorAll('[data-block], [data-custom-section]');

	if (allSections.length === 0) return;

	const blockData = Array.from(allSections).map(block => ({
		id: block.id,
		title: block.dataset.blockTitle,
	}))
	.filter(item => item.title)
	.filter((item, index, self) => 
		index === self.findIndex((t) => t.title === item.title || t.id === item.id)
	);

	// Blocks we want to apply the mobile toggle to
	const toggleSelectors = [
		'[data-name="banner-skip-links"]',
		'[data-name="banner-menu-links"]'
	];
	const toggleBlocks = toggleSelectors
		.map(sel => document.querySelector(sel))
		.filter(Boolean);

	if (toggleBlocks.length === 0) return;

	// Populate each block if needed (only for skip-links block)
	toggleBlocks.forEach(block => {
		if (block.dataset.name === 'banner-skip-links') {
			const list = block.querySelector('ul');
			if (!list) return;

			// Hide block if no items
			if (blockData.length === 0) {
				block.classList.add('hidden');
				return;
			}

			// Create the navigation list dynamically
			blockData.forEach(item => {
				const li = document.createElement('li');
				li.classList.add('group');

				const link = document.createElement('a');
				link.classList.add('skip-link-with-hidden-arrow');
				link.href = `#${item.id}`;
				link.setAttribute('data-scroll', 'scroll-to');

				const svg = document.createElement('span');
				svg.innerHTML = '<svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="11.3125" cy="11.3137" r="7.5" fill="#EBB700" transform-box="fill-box" class="transform scale-0 group-hover:scale-100 transition-transform duration-400 ease-out pointer-events-none" transform-origin="center"/><circle cx="11.3125" cy="11.3137" r="7.5" stroke="#EBB700" fill="none" /><path d="M11.6175 8.45759L14.7344 11.3147M14.7344 11.3147L11.6175 14.1719M14.7344 11.3147L7.87723 11.3147" stroke="white" stroke-linecap="round" stroke-linejoin="round"/></svg>';
				svg.style.transform = 'rotate(90deg)';

				link.appendChild(svg);
				link.appendChild(document.createTextNode(item.title));
				li.appendChild(link);
				list.appendChild(li);
			});
		}
	});

	// MOBILE toggle + footer hiding
	if (window.innerWidth < 768) {
		toggleBlocks.forEach(block => {
			const list = block.querySelector('ul');
			if (!list) return;

			gsap.set(list, { autoAlpha: 0, height: 0, marginBottom: -20 });

			const tl = gsap.timeline({ paused: true })
				.fromTo(list, { autoAlpha: 0, height: 0, marginBottom: -20 }, { autoAlpha: 1, height: "auto", marginBottom: 0, duration: 0.3, ease: "power1.out" });

			const svgParent = block.querySelector('svg')?.parentElement;
			if (svgParent) {
				tl.to(svgParent, { rotate: '180deg', duration: 0.35, ease: "power1.out" }, "<");
			}

			block.addEventListener("click", () => {
				block.classList.toggle('active');
				block.classList.contains('active') ? tl.play() : tl.reverse();
			});
		});

		const footer = document.querySelector('#site-footer');
		if (footer) {
			function isFooterInView() {
				const rect = footer.getBoundingClientRect();
				const vh = window.innerHeight || document.documentElement.clientHeight;
				return rect.top < vh && rect.bottom > 0;
			}

			const lenis = new Lenis();

			lenis.on('scroll', () => {
				const hide = isFooterInView();
				toggleBlocks.forEach(block => {
					gsap.to(block, { autoAlpha: hide ? 0 : 1, pointerEvents: hide ? 'none' : 'auto', duration: 0.3, ease: "linear" });
				});
			});

			function raf(time) {
				lenis.raf(time);
				requestAnimationFrame(raf);
			}
			requestAnimationFrame(raf);
		}
	}
}

export { bannerSkipLinks, bannerStrategicPlan };
